import type { ReactElement } from "react";
import { Box, Drawer, Typography, Button } from "@mui/material";
import FormProvider from "src/components/hook-form";
import FormErrorsDrawer from "src/sections/@dashboard/common/form/FormErrorsDrawer";
import Scrollbar from "src/components/scrollbar";
import useResponsive from "src/hooks/useResponsive";

export type ConfirmModalProps = {
  onAction: VoidFunction;
  title?: string;
  children: ReactElement,
  onClose: VoidFunction;
  methods: any;
  /** Ширина Drawer (px или строка, напр. '800px') */
  drawerWidth?: number | string;
  /** Ошибки валидации для FormErrorsDrawer (при throw ValidationErrorsError из onAction) */
  validationErrors?: { path: string; message: string }[];
  errorsDrawerOpen?: boolean;
  onCloseErrorsDrawer?: () => void;
  formFieldLabels?: Record<string, string>;
  /** Текст кнопки действия вместо "Continue" */
  actionLabel?: string;
}

export const FormDrawerModal = ({
  onAction,
  title,
  onClose,
  children,
  methods,
  drawerWidth = 500,
  validationErrors = [],
  errorsDrawerOpen = false,
  onCloseErrorsDrawer = () => {},
  formFieldLabels = {},
  actionLabel,
}: ConfirmModalProps): ReactElement => {
  const isDesktop = useResponsive('up', 'md');
  const width = typeof drawerWidth === 'number' ? `${drawerWidth}px` : drawerWidth;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isDesktop ? width : '100%'
        }
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        >
          <Typography variant="h6" component="div" sx={{ p: 2 }}>
            {title || 'Do you want to continue?'}
          </Typography>
            <FormProvider methods={methods}>
              <Box flexGrow={1} overflow="hidden">
                <Scrollbar>
                  {children}
                </Scrollbar>
              </Box>
            </FormProvider>

          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
            <Button color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={onAction} sx={{ ml: 1 }}>
              {actionLabel || 'Continue'}
            </Button>
          </Box>
      </Box>
      <FormErrorsDrawer
        open={errorsDrawerOpen}
        onClose={onCloseErrorsDrawer}
        errors={validationErrors}
        onFocusField={(path) => methods?.setFocus?.(path)}
        fieldLabels={formFieldLabels}
      />
    </Drawer>
  );
};
